import { useEffect, useState } from 'react';

/**
 * Live countdown to a target ISO timestamp. Updates every second.
 * Returns one of:
 *   - `null` when the target is null/invalid
 *   - `"running…"` when the target is in the past
 *   - `"in M:SS"` (≤ 1 hour) or `"in Hh Mm"` (over 1 hour) otherwise
 */
export function useCountdown(targetIso: string | null | undefined): string | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!targetIso) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [targetIso]);

  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  if (!Number.isFinite(target)) return null;

  const remaining = Math.floor((target - now) / 1000);
  if (remaining <= 0) return 'running…';

  if (remaining < 3600) {
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    return `in ${m}:${s.toString().padStart(2, '0')}`;
  }
  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  return m > 0 ? `in ${h}h ${m}m` : `in ${h}h`;
}
